import { getUrlAsync } from './service-urls';
import { logError } from './betterstack-logger';
/**
 * Auth API Client
 * REQ-006a: Centralized authentication through standalone Auth API service
 *
 * Uses HttpOnly cookies for refresh tokens (XSS-immune)
 * Access tokens stored in memory only
 */

// Standalone Auth Service URL is resolved at call time via getUrlAsync (OPS-391) —
// the frozen sync export evaluates at module load and serves a stale build-time
// fallback that dies at the Railway consolidation cutover.
const getAuthApiUrl = () => getUrlAsync('AUTH_SERVICE_URL');

// In-memory token storage (never exposed to localStorage/XSS)
let cachedToken = null
let tokenExpiry = null

/**
 * Log auth events (sanitized - no token values)
 */
function logAuth(event, success, error = null) {
  const log = {
    timestamp: new Date().toISOString(),
    event,
    success,
    ...(error && { error: typeof error === 'string' ? error : error.message })
  }
  console.log(`🔐 Auth:`, JSON.stringify(log))
}

/**
 * Check if token is expired or about to expire
 */
function isTokenExpired() {
  if (!cachedToken || !tokenExpiry) return true
  
  const now = Date.now()
  const expiryMs = new Date(tokenExpiry).getTime()
  
  // Consider expired if within 1 minute of expiry
  return now >= expiryMs - 60000
}

export const authAPI = {
  /**
   * Login with email/password
   * Sets HttpOnly refresh cookie automatically
   * @returns {Promise<{accessToken: string, expiresAt: string, user: object}>}
   */
  async login(email, password) {
    try {
      const AUTH_API_URL = await getAuthApiUrl()
      const res = await fetch(`${AUTH_API_URL}/auth/login`, {
        method: 'POST',
        credentials: 'include', // Required for HttpOnly cookies
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      })
      
      if (!res.ok) {
        const error = await res.json().catch(() => ({ message: 'Login failed' }))
        logAuth('login', false, error.message)
        throw new Error(error.message || 'Login failed')
      }
      
      const data = await res.json()
      cachedToken = data.accessToken
      tokenExpiry = data.expiresAt
      
      logAuth('login', true)
      return data
    } catch (error) {
      logAuth('login', false, error)
      logError('auth-api-login-failed', { error: error.message })
      throw error
    }
  },

  /**
   * Logout and clear session
   */
  async logout() {
    try {
      const AUTH_API_URL = await getAuthApiUrl()
      await fetch(`${AUTH_API_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      })
      logAuth('logout', true)
    } catch (error) {
      logAuth('logout', false, error)
      logError('auth-api-logout-failed', { error: error.message })
    } finally {
      // Always clear local token state
      cachedToken = null
      tokenExpiry = null
    }
  },

  /**
   * Refresh access token using HttpOnly cookie
   * @returns {Promise<{accessToken: string, expiresAt: string}>}
   */
  async refresh() {
    try {
      const AUTH_API_URL = await getAuthApiUrl()
      const res = await fetch(`${AUTH_API_URL}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include'
      })
      
      if (!res.ok) {
        cachedToken = null
        tokenExpiry = null
        logAuth('refresh', false, 'Session expired')
        throw new Error('Session expired')
      }
      
      const data = await res.json()
      cachedToken = data.accessToken
      tokenExpiry = data.expiresAt
      
      logAuth('refresh', true)
      return data
    } catch (error) {
      cachedToken = null
      tokenExpiry = null
      logAuth('refresh', false, error)
      logError('auth-api-refresh-failed', { error: error.message })
      throw error
    }
  },

  /**
   * Get current valid token
   * Auto-refreshes if expired
   * @param {boolean} redirectOnFail - If true, redirect to login on failure (default: false)
   * @returns {Promise<string>} Access token
   */
  async getToken(redirectOnFail = false) {
    // If token is valid, return it
    if (!isTokenExpired()) {
      return cachedToken
    }
    
    // Try to refresh
    try {
      await this.refresh()
      return cachedToken
    } catch (err) {
      logAuth('getToken', false, err.message)
      logError('auth-api-get-token-failed', { error: err.message })
      if (redirectOnFail) {
        window.location.href = '/login?session=expired'
      }
      throw err
    }
  },
  
  /**
   * Check if there's a valid auth-service session without redirecting
   * @returns {boolean}
   */
  hasValidSession() {
    return !isTokenExpired() && !!cachedToken
  },

  /**
   * Get current user session info
   * @returns {Promise<{user: object, expiresAt: string} | null>}
   */
  async getSession() {
    try {
      const token = cachedToken || await this.getToken()
      const AUTH_API_URL = await getAuthApiUrl()
      const res = await fetch(`${AUTH_API_URL}/auth/session`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      
      if (!res.ok) {
        logAuth('getSession', false, 'Invalid session')
        return null
      }
      
      const data = await res.json()
      logAuth('getSession', true)
      return data
    } catch (error) {
      logAuth('getSession', false, error)
      logError('auth-api-get-session-failed', { error: error.message })
      return null
    }
  },

  /**
   * Check if user is authenticated (has valid cached token)
   * @returns {boolean}
   */
  isAuthenticated() {
    return !isTokenExpired()
  },

  /**
   * Get cached token without refresh (for checking state)
   * @returns {string | null}
   */
  getCachedToken() {
    return cachedToken
  },

  /**
   * Clear cached token state (for manual logout)
   */
  clearToken() {
    cachedToken = null
    tokenExpiry = null
  },

  // ==================== OAuth Methods ====================

  /**
   * Initiate OAuth connection for a social platform
   * @param {string} platform - Platform name (youtube, instagram, facebook, tiktok)
   * @returns {Promise<{authUrl: string, state: string}>}
   */
  async connectSocialAccount(platform, supabaseToken = null) {
    try {
      // Use provided Supabase token or try our own token system
      const token = supabaseToken || await this.getToken()
      
      const AUTH_API_URL = await getAuthApiUrl()
      const res = await fetch(`${AUTH_API_URL}/api/oauth/connect/${platform}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      })
      
      if (!res.ok) {
        const error = await res.json().catch(() => ({ error: 'OAuth initiation failed' }))
        logAuth('oauth_connect', false, error.error || error.message)
        throw new Error(error.error || 'Failed to initiate OAuth')
      }
      
      const data = await res.json()
      logAuth('oauth_connect', true)
      return data
    } catch (error) {
      logAuth('oauth_connect', false, error)
      logError('auth-api-oauth-connect-failed', { error: error.message, platform })
      throw error
    }
  },

  /**
   * Get list of connected social accounts
   * @param {string} supabaseToken - Optional Supabase access token (recommended)
   * @returns {Promise<{accounts: Array}>}
   */
  async getSocialAccounts(supabaseToken = null) {
    try {
      // Use provided Supabase token or try our own token system
      const token = supabaseToken || await this.getToken()
      
      const AUTH_API_URL = await getAuthApiUrl()
      const res = await fetch(`${AUTH_API_URL}/api/oauth/accounts`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      })
      
      if (!res.ok) {
        const error = await res.json().catch(() => ({ error: 'Failed to fetch accounts' }))
        logAuth('oauth_list', false, error.error)
        throw new Error(error.error || 'Failed to fetch accounts')
      }
      
      const data = await res.json()
      logAuth('oauth_list', true)
      return data
    } catch (error) {
      logAuth('oauth_list', false, error)
      logError('auth-api-oauth-list-failed', { error: error.message })
      throw error
    }
  },

  /**
   * Disconnect a social account
   * @param {string} platform - Platform name (youtube, instagram, facebook)
   * @param {string} accountId - Account UUID to disconnect
   * @param {string} supabaseToken - Optional Supabase access token (recommended)
   * @returns {Promise<{success: boolean}>}
   */
  async disconnectSocialAccount(platform, accountId, supabaseToken = null) {
    try {
      // Use provided Supabase token or try our own token system
      const token = supabaseToken || await this.getToken()
      
      const AUTH_API_URL = await getAuthApiUrl()
      const res = await fetch(`${AUTH_API_URL}/api/oauth/disconnect/${platform}/${accountId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      })
      
      if (!res.ok) {
        const error = await res.json().catch(() => ({ error: 'Disconnect failed' }))
        logAuth('oauth_disconnect', false, error.error)
        throw new Error(error.error || 'Failed to disconnect account')
      }
      
      const data = await res.json()
      logAuth('oauth_disconnect', true)
      return data
    } catch (error) {
      logAuth('oauth_disconnect', false, error)
      logError('auth-api-oauth-disconnect-failed', { error: error.message, platform, accountId })
      throw error
    }
  },

  /**
   * Refresh tokens for a social account
   * @param {string} accountId - Account UUID
   * @param {string} supabaseToken - Optional Supabase access token (recommended)
   * @returns {Promise<{success: boolean}>}
   */
  async refreshSocialTokens(accountId, supabaseToken = null) {
    try {
      // Use provided Supabase token or try our own token system
      const token = supabaseToken || await this.getToken()
      
      const AUTH_API_URL = await getAuthApiUrl()
      const res = await fetch(`${AUTH_API_URL}/api/oauth/refresh/${accountId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      })
      
      if (!res.ok) {
        const error = await res.json().catch(() => ({ error: 'Token refresh failed' }))
        logAuth('oauth_refresh', false, error.error)
        throw new Error(error.error || 'Failed to refresh tokens')
      }
      
      const data = await res.json()
      logAuth('oauth_refresh', true)
      return data
    } catch (error) {
      logAuth('oauth_refresh', false, error)
      logError('auth-api-oauth-refresh-failed', { error: error.message, accountId })
      throw error
    }
  }
}

export default authAPI
